"use client";

import type { ComponentProps, ElementRef } from "react";

import { AnimatePresence, m } from "motion/react";
import { forwardRef, useImperativeHandle, useRef, useEffect } from "react";

import useViewportHeightListeners from "~/common/hooks/dom/use-viewport-height";
import type { ChatMessage as ChatMessageType } from "~/chat/types/chat";

import { chatLayoutStyle } from "./chat.style";

import useChatAnchorScroll from "../hooks/use-chat-anchor-scroll";

const ChatLayoutRoot = forwardRef<ElementRef<"div">, ComponentProps<"div">>(
  ({ className, ...props }, ref) => {
    useViewportHeightListeners();

    return (
      <div
        {...props}
        ref={ref}
        id="chat-layout"
        className={chatLayoutStyle.container({ className })}
      >
        {props.children}
      </div>
    );
  },
);
ChatLayoutRoot.displayName = "ChatLayoutRoot";

type ChatLayoutScrollProps = ComponentProps<"div"> & {
  autoScrollDisabled?: boolean;
  messages?: ChatMessageType[];
  conversationId?: number;
};

const ChatLayoutScroll = forwardRef<ElementRef<"div">, ChatLayoutScrollProps>(
  (
    { className, autoScrollDisabled, messages = [], conversationId, ...props },
    ref,
  ) => {
    const listRef = useRef<HTMLDivElement | null>(null);

    useChatAnchorScroll({
      elementRef: listRef,
      messages,
      autoScrollDisabled,
      conversationId,
    });

    useEffect(() => {
      if (!autoScrollDisabled && messages.length > 0) {
        listRef.current?.scrollTo({
          top: listRef.current.scrollHeight,
          behavior: "auto",
        });
      }
    }, [autoScrollDisabled, messages.length]);

    useImperativeHandle(ref, () => {
      if (!listRef.current) {
        throw new Error("listRef is not assigned yet");
      }
      return listRef.current;
    }, []);

    return (
      <div
        {...props}
        ref={listRef}
        className={chatLayoutStyle.scrollArea({ className })}
      >
        <ChatLayoutScrollContent>
          <AnimatePresence mode="popLayout">{props.children}</AnimatePresence>
        </ChatLayoutScrollContent>
      </div>
    );
  },
);
ChatLayoutScroll.displayName = "ChatLayoutScroll";

const ChatLayoutScrollContent = forwardRef<
  ElementRef<"div">,
  ComponentProps<"div">
>(({ className, ...props }, ref) => {
  return (
    <div
      {...props}
      ref={ref}
      id="chat-scroll-content"
      className={chatLayoutStyle.content({ className })}
    />
  );
});
ChatLayoutScrollContent.displayName = "ChatLayoutScrollContent";

const ChatLayoutMessagesList = forwardRef<
  ElementRef<typeof m.div>,
  ComponentProps<typeof m.div>
>(({ className, ...props }, ref) => {
  return (
    <m.div
      {...props}
      ref={ref}
      className={chatLayoutStyle.messageList({ className })}
    />
  );
});
ChatLayoutMessagesList.displayName = "ChatLayoutMessagesList";

const ChatLayoutFormArea = forwardRef<
  ElementRef<"form">,
  ComponentProps<"form">
>(({ className, ...props }, ref) => {
  return (
    <form
      {...props}
      ref={ref}
      className={chatLayoutStyle.formArea({ className })}
    >
      <m.div
        key={props.key}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0, transition: { delay: 0.1 } }}
        className={chatLayoutStyle.formAreaContent({})}
      >
        {props.children}
      </m.div>
    </form>
  );
});
ChatLayoutFormArea.displayName = "ChatLayoutFormArea";

export {
  ChatLayoutRoot,
  ChatLayoutScroll,
  ChatLayoutScrollContent,
  ChatLayoutMessagesList,
  ChatLayoutFormArea,
};
